import { action, computed } from "@ember/object"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { Planbook, PlanbookDateAnnotation } from "@shared/models/planbook"
import type { PlanbookCalendarDateCombined } from "@shared/models/planbook-calendar-date-combined"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type { Moment } from "cc-frontend/utils/extended-moment"
import moment from "cc-frontend/utils/extended-moment"
import _ from "lodash"
import * as R from "remeda" // tree-shaking supported!
import styles from "./day-marker.module.css"

interface Args {
  canShowDayMenu: boolean
  annotation: PlanbookDateAnnotation
  rotationCalendar: RotationCalendar
  planbook: Planbook
  date: string
  dateString: string
  isFilteringClasses: boolean
  unitId: string
  hiddenCourseIds: Array<string>
  planbookDateCombined: PlanbookCalendarDateCombined
}

export default class DayMarker extends Component<Args> {
  readonly styles = styles

  dayMenuConstraints = [{ to: "window", pin: ["left"] }]

  @tracked
  canShowDayMenu = false

  get componentId() {
    return "annotation-card-" + this.args.annotation.id
  }

  @computed("args.date", "args.rotationCalendar.attributes.schoolDays")
  get dateStrings() {
    let validSchoolDays = this.args.rotationCalendar.attributes.schoolDays
    let date = this.args.date
    let startDate = moment(date).startOf("week")
    let endDate = moment(date).endOf("week")

    let range = moment.range(startDate, endDate)

    let acc: Moment[] = []
    Array.from(range.by("days")).map((moment) => {
      if (_.includes(validSchoolDays, moment.weekday())) acc.push(moment)
    })
    let newDates = _.uniq(_.map(acc, (m) => m.format("YYYY-MM-DD")))

    return newDates
  }

  @computed("args.rotationCalendar.attributes.dates", "args.planbook.attributes.calendar.datesOff")
  get dayCount() {
    let days = this.args.rotationCalendar.attributes.dates
    let currentDay = this.args.dateString
    let datesOff = this.args.planbook.attributes.calendar.datesOff
    if (datesOff.includes(currentDay)) return "Off"
    let dayIndex = R.pipe(
      days,
      R.map((day) => day.attributes.dateString),
      R.reject((day) => datesOff.includes(day)),
      R.sortBy((day) => day),
      R.findIndex((day) => day === currentDay)
    )

    return dayIndex + 1
  }

  @action
  toggleDayMenu() {
    this.canShowDayMenu = !this.canShowDayMenu
  }
}
